import type {
  ViewFolderId,
  ViewId,
} from "@redotech/effect-api-shared/ids/definitions";
import {
  ViewFolderId as ViewFolderIdSchema,
  ViewId as ViewIdSchema,
} from "@redotech/effect-api-shared/ids/definitions";
import type {
  FolderResponse,
  ViewResponse,
} from "@redotech/effect-api-shared/rpc/view-folder";
import type { EffectApiContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import type { FilterGroup } from "@redotech/redo-model/views/views";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { createCollection } from "@tanstack/react-db";
import type { QueryClient } from "@tanstack/react-query";
import { conversationCountsQueryKey } from "../hooks/use-conversation-counts";
import { catchApiErrors } from "./error-handling";

const FIVE_MINUTES_MS = 5 * 60 * 1000;

export const viewQueryKeys: { readonly all: readonly ["views"] } = {
  all: ["views"] as const,
};

export const folderQueryKeys: { readonly all: readonly ["folders"] } = {
  all: ["folders"] as const,
};

interface CollectionOptions {
  queryClient: QueryClient;
  effectApi: EffectApiContext;
}

export function createViewsCollection({
  queryClient,
  effectApi,
}: CollectionOptions) {
  return createCollection<ViewResponse, ViewId>(
    queryCollectionOptions({
      queryKey: viewQueryKeys.all,
      queryFn: async (): Promise<ViewResponse[]> => {
        const client = effectApi.getClient();
        const result = await effectApi.runPromise(
          catchApiErrors(client.viewFolder.getViewsAndFolders({})),
        );
        return [...result.views];
      },
      getKey: (item) => item.id,
      queryClient,
      staleTime: FIVE_MINUTES_MS,
      onInsert: async ({ transaction }) => {
        const client = effectApi.getClient();
        for (const mutation of transaction.mutations) {
          const view = mutation.modified;
          await effectApi.runPromise(
            catchApiErrors(
              client.viewFolder.createView({
                payload: {
                  id: view.id,
                  name: view.name,
                  filterGroup: (view.filterGroup as FilterGroup) ?? null,
                  folderId: view.folderId ?? null,
                },
              }),
            ),
          );
        }
        void queryClient.invalidateQueries({
          queryKey: conversationCountsQueryKey,
        });
      },
      onUpdate: async ({ transaction }) => {
        const client = effectApi.getClient();
        for (const mutation of transaction.mutations) {
          const view = mutation.modified;
          await effectApi.runPromise(
            catchApiErrors(
              client.viewFolder.updateView({
                path: { id: ViewIdSchema.make(String(mutation.key)) },
                payload: {
                  name: view.name,
                  filterGroup: (view.filterGroup as FilterGroup) ?? null,
                },
              }),
            ),
          );
        }
        void queryClient.invalidateQueries({
          queryKey: conversationCountsQueryKey,
        });
      },
      onDelete: async ({ transaction }) => {
        const client = effectApi.getClient();
        for (const mutation of transaction.mutations) {
          await effectApi.runPromise(
            catchApiErrors(
              client.viewFolder.deleteView({
                path: { id: ViewIdSchema.make(String(mutation.key)) },
              }),
            ),
          );
        }
        void queryClient.invalidateQueries({
          queryKey: conversationCountsQueryKey,
        });
      },
    }),
  );
}

export function createFoldersCollection({
  queryClient,
  effectApi,
}: CollectionOptions) {
  return createCollection<FolderResponse, ViewFolderId>(
    queryCollectionOptions({
      queryKey: folderQueryKeys.all,
      queryFn: async (): Promise<FolderResponse[]> => {
        const client = effectApi.getClient();
        const result = await effectApi.runPromise(
          catchApiErrors(client.viewFolder.getViewsAndFolders({})),
        );
        return [...result.folders];
      },
      getKey: (item) => item.id,
      queryClient,
      staleTime: FIVE_MINUTES_MS,
      onInsert: async ({ transaction }) => {
        const client = effectApi.getClient();
        for (const mutation of transaction.mutations) {
          const folder = mutation.modified;
          await effectApi.runPromise(
            catchApiErrors(
              client.viewFolder.createFolder({
                payload: {
                  id: folder.id,
                  name: folder.name,
                  viewIds: [],
                  isPrivate: folder.isPrivate,
                },
              }),
            ),
          );
        }
      },
      onUpdate: async ({ transaction }) => {
        const client = effectApi.getClient();
        for (const mutation of transaction.mutations) {
          const folder = mutation.modified;

          await effectApi.runPromise(
            catchApiErrors(
              client.viewFolder.updateFolder({
                path: { id: ViewFolderIdSchema.make(String(mutation.key)) },
                payload: { name: folder.name, viewIds: [] },
              }),
            ),
          );
        }
      },
      onDelete: async ({ transaction }) => {
        const client = effectApi.getClient();
        for (const mutation of transaction.mutations) {
          await effectApi.runPromise(
            catchApiErrors(
              client.viewFolder.deleteFolder({
                path: { id: ViewFolderIdSchema.make(String(mutation.key)) },
              }),
            ),
          );
        }
      },
    }),
  );
}

export type ViewsCollection = ReturnType<typeof createViewsCollection>;
export type FoldersCollection = ReturnType<typeof createFoldersCollection>;
